import { type Dispatch } from 'redux';

import { getCurrentConference } from '../base/conference';
import { openDialog } from '../base/dialog';
import { isLocalParticipantModerator } from '../base/participants/functions';
import { isInBreakoutRoom } from '../breakout-rooms/functions.any';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';

import { SET_FOCUS_MODE_ENABLED } from './actionTypes';
import { FOCUS_MODE_METADATA_KEY, SET_FOCUS_MODE_MESSAGE_TYPE } from './constants';
import { isFocusModeAllowedForMeeting, isFocusModeEnabled } from './functions';
import logger from './logger';

/**
 * Updates the local Focus Mode state. Every other part of the feature reads
 * from here.
 *
 * @param {boolean} enabled - Whether Focus Mode is on.
 * @returns {{
 *     type: SET_FOCUS_MODE_ENABLED,
 *     enabled: boolean
 * }}
 */
export function setFocusModeEnabled(enabled: boolean) {
    return {
        type: SET_FOCUS_MODE_ENABLED,
        enabled: Boolean(enabled)
    };
}

/**
 * Writes Focus Mode into the room metadata when the deployment supports it, so
 * the setting outlives the moderator who turned it on.
 *
 * @param {Object} conference - The current conference.
 * @param {boolean} enabled - The state to persist.
 * @private
 * @returns {void}
 */
function _persistFocusMode(conference, enabled) {
    try {
        const metadataHandler = conference?.getMetadataHandler?.();

        if (metadataHandler?.isSupported?.()) {
            metadataHandler.setMetadata(FOCUS_MODE_METADATA_KEY, { enabled: Boolean(enabled) });
        }
    } catch (e) {
        logger.warn('Failed to persist Focus Mode in the room metadata', e);
    }
}

/**
 * Sends the current Focus Mode state over the data channel. Without a target it
 * broadcasts to the room; with one it addresses a single endpoint, which is how
 * late joiners are brought up to date.
 *
 * @param {Object} conference - The current conference.
 * @param {boolean} enabled - The state to announce.
 * @param {string} [to] - The endpoint to address, or the whole room when omitted.
 * @private
 * @returns {void}
 */
function _sendFocusModeMessage(conference, enabled, to) {
    if (typeof conference?.sendMessage !== 'function') {
        return;
    }

    const payload = {
        type: SET_FOCUS_MODE_MESSAGE_TYPE,
        enabled: Boolean(enabled),
        senderId: conference.myUserId()
    };

    try {
        to ? conference.sendMessage(payload, to) : conference.sendMessage(payload);
    } catch (e) {
        logger.warn('Failed to announce Focus Mode', e);
    }
}

/**
 * Opens the note explaining what Focus Mode is. Shown on every switch-on,
 * because "the other tiles vanished" is not self-explanatory to anyone who has
 * not met the feature before.
 *
 * The components barrel is required lazily: it resolves to the platform's
 * dialog, and importing it at module scope would make this action module - which
 * middleware imports - depend on React components.
 *
 * @returns {Function}
 */
function _showFocusModeInfo() {
    return (dispatch: Dispatch<any>) => {
        const { FocusModeInfoDialog, FOCUS_MODE_INFO_DIALOG_PROPS } = require('./components');

        dispatch(openDialog(FocusModeInfoDialog, FOCUS_MODE_INFO_DIALOG_PROPS));
    };
}

/**
 * Turns Focus Mode on or off for the whole meeting. Hosts only - the state is
 * broadcast to everybody and persisted in the room metadata where available.
 *
 * @param {boolean} enabled - The desired Focus Mode state.
 * @returns {Function}
 */
export function toggleFocusMode(enabled: boolean) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const conference = getCurrentConference(state);

        if (!conference || !isLocalParticipantModerator(state)) {
            return;
        }

        dispatch(setFocusModeEnabled(enabled));

        _persistFocusMode(conference, enabled);
        _sendFocusModeMessage(conference, enabled);

        if (enabled && isLocalParticipantModerator(state)) {
            dispatch(_showFocusModeInfo());

            return;
        }

        dispatch(showNotification({
            titleKey: enabled ? 'notify.focusModeEnabledTitle' : 'notify.focusModeDisabledTitle',
            descriptionKey: enabled
                ? 'notify.focusModeEnabledDescription'
                : 'notify.focusModeDisabledDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    };
}

/**
 * Announces the current Focus Mode state to a single participant. Called by the
 * host's client when somebody joins, so that they do not miss the broadcast
 * that went out before they arrived.
 *
 * @param {string} participantId - The endpoint to bring up to date.
 * @returns {Function}
 */
export function announceFocusModeTo(participantId: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();

        if (!participantId || !isLocalParticipantModerator(state) || !isFocusModeEnabled(state)) {
            return;
        }

        _sendFocusModeMessage(getCurrentConference(state), true, participantId);
    };
}

/**
 * Applies a Focus Mode decision that arrived from a host, and tells the local
 * user about it only when their own view actually changes.
 *
 * @param {boolean} enabled - The incoming Focus Mode state.
 * @returns {Function}
 */
export function applyRemoteFocusMode(enabled: boolean) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const nextEnabled = Boolean(enabled);

        if (isFocusModeEnabled(state) === nextEnabled) {
            return;
        }

        dispatch(setFocusModeEnabled(nextEnabled));

        // The explainer is for the people whose view actually changes. A
        // co-host's view does not change, but they still need to know the
        // meeting's state moved under them, so they get the lighter notice.
        if (nextEnabled && !isLocalParticipantModerator(state)) {
            dispatch(_showFocusModeInfo());

            return;
        }

        dispatch(showNotification({
            titleKey: nextEnabled ? 'notify.focusModeEnabledTitle' : 'notify.focusModeDisabledTitle',
            descriptionKey: nextEnabled
                ? 'notify.focusModeEnabledDescription'
                : 'notify.focusModeDisabledDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    };
}

/**
 * Reads Focus Mode back out of the room metadata. Runs on join and whenever the
 * metadata changes, so a participant who arrives long after the toggle was
 * flipped still lands in the right state.
 *
 * @returns {Function}
 */
export function reconcileFocusModeFromMetadata() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState());

        try {
            const metadata = conference?.getMetadataHandler?.()?.getMetadata?.();
            const decided = metadata?.[FOCUS_MODE_METADATA_KEY]?.enabled;

            if (typeof decided === 'boolean') {
                dispatch(applyRemoteFocusMode(decided));
            }
        } catch (e) {
            logger.warn('Failed to read the Focus Mode room metadata', e);
        }
    };
}

/**
 * Enables Focus Mode automatically for meetings that have it switched on in the
 * backend settings. This is a silent path: the moderator already opted into it
 * while scheduling/editing the meeting, so the join flow should just apply it.
 *
 * @returns {Function}
 */
export function autoEnableFocusMode() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const conference = getCurrentConference(state);
        const metadataDecision
            = conference?.getMetadataHandler?.()?.getMetadata?.()?.[FOCUS_MODE_METADATA_KEY]?.enabled;

        if (!conference
            || !isLocalParticipantModerator(state)
            || !isFocusModeAllowedForMeeting(state)
            || isInBreakoutRoom(state)
            || typeof metadataDecision === 'boolean'
            || isFocusModeEnabled(state)) {
            return;
        }

        dispatch(setFocusModeEnabled(true));
        _persistFocusMode(conference, true);
        _sendFocusModeMessage(conference, true);
    };
}
